import { useState } from "react";
import { Share2, Download, Copy, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { useAuth } from "@/contexts/AuthContext";

function XIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

export default function ShareBar({ topic, topicId, pctA, pctB, shareCardRef }) {
  const { user, api } = useAuth();
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);
  const base = `${window.location.origin}/api/share/${topicId}`;
  const url = user ? `${base}?ref=${user.user_id}` : base;
  const caption = `"${topic.question}" — ${pctA}% ${topic.side_a_label} vs ${pctB}% ${topic.side_b_label} on Flipped View. Where do you stand?`;

  const track = (channel) => { if (user) api.post(`/topics/${topicId}/share`, { channel }).catch(() => {}); };

  const renderImage = async () => {
    const canvas = await html2canvas(shareCardRef.current, { scale: 2, backgroundColor: null, logging: false, useCORS: true, imageTimeout: 15000 });
    return new Promise((res) => canvas.toBlob(res, "image/png"));
  };

  const download = (blob) => {
    const link = document.createElement("a");
    link.download = `flippedview-${topicId}.png`;
    link.href = URL.createObjectURL(blob);
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 2000);
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
  };

  const shareResult = async () => {
    setBusy("share");
    track("image");
    try {
      const blob = await renderImage();
      const file = new File([blob], `flippedview-${topicId}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: topic.question, text: caption, url });
        toast.success("Shared!");
      } else {
        download(blob);
        const ok = await copyText(`${caption} ${url}`);
        toast.success(ok ? "Image downloaded + caption copied. Paste it into your post!" : "Image downloaded. Copy the caption below.");
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Could not share right now");
    } finally {
      setBusy(null);
    }
  };

  const downloadOnly = async () => {
    setBusy("download");
    try { download(await renderImage()); toast.success("Image downloaded!"); }
    catch { toast.error("Could not generate image"); }
    finally { setBusy(null); }
  };

  const copyCaption = async () => {
    track("caption");
    const ok = await copyText(`${caption} ${url}`);
    if (ok) { setCopied(true); toast.success("Caption copied!"); setTimeout(() => setCopied(false), 1800); }
    else toast.error("Clipboard blocked by browser");
  };

  const copyLink = async () => {
    track("link");
    if (navigator.share && !navigator.canShare) {
      try { await navigator.share({ title: topic.question, url }); return; } catch {}
    }
    (await copyText(url)) ? toast.success("Link copied!") : toast.error("Clipboard blocked by browser");
  };

  const encoded = encodeURIComponent(caption);
  const xUrl = `https://twitter.com/intent/tweet?text=${encoded}&url=${encodeURIComponent(url)}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(`${caption} ${url}`)}`;
  const small = "flex items-center gap-1.5 text-xs text-neutral hover:text-dark font-medium transition-colors disabled:opacity-50";

  return (
    <div className="mt-5" data-testid="share-bar">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          onClick={shareResult}
          disabled={!!busy}
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-dark rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
          data-testid="share-result-btn"
        >
          <Share2 className="w-3.5 h-3.5" /> {busy === "share" ? "Preparing…" : "Share result"}
        </button>
        <a href={xUrl} target="_blank" rel="noreferrer" onClick={() => track("x")} className="p-2 rounded-full bg-gray-100 text-dark hover:bg-dark hover:text-white transition-colors" aria-label="Share on X" data-testid="share-x-btn">
          <XIcon className="w-3.5 h-3.5" />
        </a>
        <a href={waUrl} target="_blank" rel="noreferrer" onClick={() => track("whatsapp")} className="p-2 rounded-full bg-gray-100 text-dark hover:bg-[#25D366] hover:text-white transition-colors" aria-label="Share on WhatsApp" data-testid="share-whatsapp-btn">
          <WhatsAppIcon className="w-3.5 h-3.5" />
        </a>
      </div>
      <div className="flex items-center justify-center gap-4 mt-3">
        <button onClick={copyCaption} className={small} data-testid="copy-caption-btn">
          {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />} Copy caption
        </button>
        <span className="text-gray-200">|</span>
        <button onClick={copyLink} className={small} data-testid="share-btn">
          <Link2 className="w-3.5 h-3.5" /> Copy link
        </button>
        <span className="text-gray-200">|</span>
        <button onClick={downloadOnly} disabled={!!busy} className={small} data-testid="download-card-btn">
          <Download className="w-3.5 h-3.5" /> {busy === "download" ? "Generating…" : "Download image"}
        </button>
      </div>
      <p className="mt-3 text-center text-[11px] text-neutral italic max-w-md mx-auto line-clamp-2" data-testid="share-caption-preview">{caption}</p>
    </div>
  );
}
